import { useEffect, useRef, useState } from "react";
import {
  ActivityIndicator,
  Platform,
  StyleSheet,
  TextInput,
  View
} from "react-native";

import {
  sendEmailSignInLink,
  signInRateLimitCopy
} from "@/auth/emailLogin";
import { useAuthState } from "@/auth/AuthProvider";
import { subscribeToLoginRequestApproval } from "@/auth/loginRequest";
import { CenteredCardBrand } from "@/components/CenteredCardBrand";
import { Button } from "@/design/Button";
import { Screen } from "@/design/primitives";
import { Muted, Text } from "@/design/Text";
import { fonts, palette, radius, space } from "@/design/tokens";
import {
  firebaseErrorCode,
  firebaseErrorMessage,
  redeemGiftCode
} from "@/firebase/callables";
import { useAppRouter } from "@/lib/appRouter";

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
type SubmissionState =
  | "idle"
  | "submitting"
  | "submitted"
  | "finishing"
  | "finishing-slow"
  | "completion-error";

function giftErrorCopy(error: unknown): string {
  const code = firebaseErrorCode(error);
  if (code === "not-found") {
    return "That code isn't recognized — check the card and try again.";
  }
  if (code === "failed-precondition") {
    return "This code's window has closed — email recitalmaster@gmail.com and we'll take care of you.";
  }
  if (code === "resource-exhausted") {
    return "Too many tries. Wait a few minutes and try again.";
  }
  if (code === "already-exists") {
    return "This welcome gift is for families creating their first Recital Master account.";
  }
  return firebaseErrorMessage(error);
}

function formattedEndDate(value: string): string {
  return new Intl.DateTimeFormat(undefined, {
    day: "numeric",
    month: "long",
    year: "numeric"
  }).format(new Date(value));
}

export default function GiftRoute() {
  const giftPreview =
    __DEV__ &&
    Platform.OS === "web" &&
    typeof window !== "undefined"
      ? new URLSearchParams(window.location.search).get("preview")
      : null;
  const submittedPreview = giftPreview === "submitted";
  const [giftCode, setGiftCode] = useState("");
  const [email, setEmail] = useState("");
  const [sentTo, setSentTo] = useState(
    submittedPreview ? "you@example.com" : ""
  );
  const [accessEndsAt, setAccessEndsAt] = useState(
    submittedPreview ? "2026-08-05T12:00:00.000Z" : ""
  );
  const [focused, setFocused] = useState<"code" | "email" | null>(null);
  const [state, setState] = useState<SubmissionState>(
    submittedPreview ? "submitted" : "idle"
  );
  const [error, setError] = useState<string | null>(null);
  const [accountIneligible, setAccountIneligible] = useState(false);
  const [switchingAccount, setSwitchingAccount] = useState(false);
  const [requestId, setRequestId] = useState<string | null>(null);
  const completionStarted = useRef(false);
  const { user, signOutUser } = useAuthState();
  const router = useAppRouter();

  const proceedAfterGiftLogin = () => {
    router.replace("/recitals");
  };

  useEffect(() => {
    if (state !== "submitted" || !requestId) return undefined;
    return subscribeToLoginRequestApproval(requestId, {
      onFinishing: () => {
        completionStarted.current = true;
        setState("finishing");
      },
      onSlow: () => setState("finishing-slow"),
      onComplete: proceedAfterGiftLogin,
      onError: () => {
        completionStarted.current = false;
        setError(
          "We lost the connection while finishing sign-in. Your gift is safe — try again."
        );
        setState("completion-error");
      }
    });
    // The listener is intentionally replaced as the request state advances.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestId, state]);

  useEffect(() => {
    if (
      state !== "submitted" ||
      completionStarted.current ||
      !sentTo ||
      user?.email?.trim().toLowerCase() !== sentTo
    ) {
      return;
    }
    // The approval link may have signed this same browser in directly and
    // consumed the handoff before this tab observed its approved snapshot.
    completionStarted.current = true;
    setState("finishing");
    proceedAfterGiftLogin();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sentTo, state, user?.email]);

  const submit = async () => {
    if (state === "submitting") return;
    const signedInEmail = user?.email?.trim() ?? "";
    const trimmedEmail = signedInEmail || email.trim();
    if (!giftCode.trim()) {
      setError("Enter your gift code.");
      return;
    }
    if (!EMAIL_PATTERN.test(trimmedEmail)) {
      setError("That doesn't look like an email — check for typos.");
      return;
    }
    setState("submitting");
    setError(null);
    setAccountIneligible(false);
    try {
      const redemption = await redeemGiftCode({
        code: giftCode,
        email: trimmedEmail
      });
      if (user) {
        setAccessEndsAt(redemption.accessExpiresAt);
        setState("finishing");
        router.replace("/recitals");
        return;
      }
      const signInRequest = await sendEmailSignInLink(trimmedEmail);
      if (signInRequest.status === "rate-limited") {
        setAccessEndsAt(redemption.accessExpiresAt);
        setError(
          `${signInRateLimitCopy(signInRequest.retryAfterSeconds)} Your gift access is saved.`
        );
        setState("idle");
        return;
      }
      setSentTo(trimmedEmail.toLowerCase());
      setAccessEndsAt(redemption.accessExpiresAt);
      setRequestId(signInRequest.requestId);
      setState("submitted");
    } catch (nextError) {
      setState("idle");
      setAccountIneligible(firebaseErrorCode(nextError) === "already-exists");
      setError(giftErrorCopy(nextError));
    }
  };

  const useAnotherAccount = async () => {
    if (switchingAccount) return;
    setSwitchingAccount(true);
    setError(null);
    setAccountIneligible(false);
    try {
      await signOutUser();
      setEmail("");
      setState("idle");
    } catch (nextError) {
      setError(firebaseErrorMessage(nextError));
    } finally {
      setSwitchingAccount(false);
    }
  };

  return (
    <Screen enter="rise">
      <View style={styles.wrap}>
        <View style={styles.card}>
          <CenteredCardBrand />

          {state === "submitted" ||
          state === "finishing" ||
          state === "finishing-slow" ||
          state === "completion-error" ? (
            <>
              <Text variant="display" style={styles.headline}>
                {state === "finishing"
                  ? "Signing you in."
                  : state === "finishing-slow"
                    ? "Still signing you in."
                    : "You're in."}
              </Text>
              <Muted style={styles.copy}>
                {state === "finishing-slow"
                  ? "This is taking longer than usual, but we’re still connected. Keep this page open."
                  : state === "finishing"
                    ? "Your email is confirmed. We’re opening your recital library."
                    : (
                      <>
                        We emailed a sign-in link to{" "}
                        <Text variant="bodyStrong" color={palette.text}>
                          {sentTo}
                        </Text>
                        . Your week runs through {formattedEndDate(accessEndsAt)}.
                      </>
                    )}
              </Muted>
              {state === "submitted" || state === "completion-error" ? (
                <Muted variant="small" style={styles.copy}>
                  Open the link and we'll bring you into the show automatically.
                  Check spam if it hasn't turned up.
                </Muted>
              ) : null}
              {state === "submitted" ? (
                <View
                  accessibilityLabel="Waiting for sign-in approval"
                  accessibilityLiveRegion="polite"
                  style={styles.waitingRow}
                >
                  <ActivityIndicator
                    size={Platform.OS === "web" ? 16 : "small"}
                    color={palette.textSoft}
                  />
                  <Muted variant="small">Waiting for approval…</Muted>
                </View>
              ) : state === "finishing" || state === "finishing-slow" ? (
                <View
                  accessibilityLabel="Signing in"
                  accessibilityLiveRegion="polite"
                  style={styles.waitingRow}
                >
                  <ActivityIndicator
                    size={Platform.OS === "web" ? 16 : "small"}
                    color={palette.textSoft}
                  />
                  <Muted variant="small">
                    {state === "finishing-slow"
                      ? "Still signing you in…"
                      : "Finishing sign-in…"}
                  </Muted>
                </View>
              ) : (
                <>
                  <Text
                    accessibilityLiveRegion="polite"
                    accessibilityRole="alert"
                    color={palette.danger}
                    style={styles.copy}
                    variant="small"
                  >
                    {error}
                  </Text>
                  <Button
                    label="Try again"
                    onPress={() => {
                      completionStarted.current = false;
                      setError(null);
                      setState("submitted");
                    }}
                    size="lg"
                    variant="secondary"
                  />
                </>
              )}
            </>
          ) : (
            <>
              <Text variant="display" style={styles.headline}>
                A week on us.
              </Text>
              <Muted style={styles.copy}>
                {user
                  ? "Enter your gift code and we'll check it for the account you're already using."
                  : "Enter your gift code and email. We'll send a sign-in link, and the show is yours for seven days."}
              </Muted>

              <View style={styles.field}>
                <Text variant="smallStrong">Gift code</Text>
                <TextInput
                  accessibilityLabel="Gift code"
                  autoCapitalize="characters"
                  autoCorrect={false}
                  onBlur={() => setFocused(null)}
                  onChangeText={setGiftCode}
                  onFocus={() => setFocused("code")}
                  placeholder="Your code"
                  placeholderTextColor={palette.faint}
                  returnKeyType="next"
                  spellCheck={false}
                  style={[styles.input, focused === "code" && styles.inputLit]}
                  value={giftCode}
                />
              </View>

              {user ? (
                <View style={styles.accountBlock}>
                  <Text variant="smallStrong">Signed in as</Text>
                  <Text color={palette.text}>{user.email ?? "Your current account"}</Text>
                  <Muted variant="small">
                    A gift can only be applied to this account while you're signed in.
                  </Muted>
                </View>
              ) : (
                <View style={styles.field}>
                  <Text variant="smallStrong">Email address</Text>
                  <TextInput
                    accessibilityLabel="Email address"
                    autoCapitalize="none"
                    autoComplete="email"
                    autoCorrect={false}
                    inputMode="email"
                    onBlur={() => setFocused(null)}
                    onChangeText={setEmail}
                    onFocus={() => setFocused("email")}
                    onSubmitEditing={() => void submit()}
                    placeholder="you@example.com"
                    placeholderTextColor={palette.faint}
                    returnKeyType="send"
                    spellCheck={false}
                    style={[styles.input, focused === "email" && styles.inputLit]}
                    textContentType="emailAddress"
                    value={email}
                  />
                </View>
              )}

              {error ? (
                <Text
                  accessibilityLiveRegion="polite"
                  accessibilityRole="alert"
                  color={palette.danger}
                  style={styles.copy}
                  variant="small"
                >
                  {error}
                </Text>
              ) : null}

              <Button
                label={user ? "Redeem for this account" : "Start my week"}
                loading={state === "submitting"}
                onPress={submit}
                size="lg"
              />
              {user ? (
                <Button
                  label={
                    accountIneligible
                      ? "Sign out and use a new email"
                      : "Sign out and use another email"
                  }
                  loading={switchingAccount}
                  onPress={useAnotherAccount}
                  size="lg"
                  variant={accountIneligible ? "secondary" : "ghost"}
                />
              ) : null}
            </>
          )}
        </View>
      </View>
    </Screen>
  );
}

const styles = StyleSheet.create({
  accountBlock: {
    backgroundColor: palette.surfaceSunken,
    borderColor: palette.hairline,
    borderRadius: radius.md,
    borderWidth: 1,
    gap: space.xs,
    paddingHorizontal: space.lg,
    paddingVertical: space.md
  },
  card: {
    backgroundColor: palette.surface,
    borderColor: palette.hairline,
    borderRadius: radius.xl,
    borderWidth: 1,
    gap: space.lg,
    maxWidth: 460,
    padding: space.xxl,
    width: "100%"
  },
  copy: {
    textAlign: "center"
  },
  field: {
    gap: space.xs
  },
  headline: {
    textAlign: "center"
  },
  input: {
    backgroundColor: palette.surfaceSunken,
    borderColor: palette.hairline,
    borderRadius: radius.md,
    borderWidth: 1,
    color: palette.text,
    fontFamily: fonts.body,
    fontSize: 16,
    marginTop: space.xs,
    paddingHorizontal: space.lg,
    paddingVertical: 14,
    ...Platform.select({ web: { outlineStyle: "none" }, default: {} })
  } as never,
  inputLit: {
    borderColor: palette.hairlineStrong
  },
  wrap: {
    alignItems: "center",
    flex: 1,
    justifyContent: "center",
    minHeight: 560,
    paddingVertical: space.xxxl
  },
  waitingRow: {
    alignItems: "center",
    flexDirection: "row",
    gap: space.sm,
    justifyContent: "center"
  }
});
